/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React, { useCallback, useState, Fragment } from 'react';
import { Grid, IconButton, makeStyles, Typography } from '@material-ui/core';
import {
  Delete as DeleteIcon,
  Description as OtherFileIcon,
  Image,
  PictureAsPdf as PdfFileIcon,
  Visibility as VisibilityIcon,
} from '@material-ui/icons';

import { useTranslation } from 'react-i18next';
import { callApi } from 'api/helpers/api.helpers';
import { humanFileSize, toEllipsis } from '../../../../shared/utils/stringUtils';
import { shortFormatDatetime } from '../../../../shared/utils/dateUtils';
import {
  getAttachmentsTypesFromCombinedFile,
  getCustomNumberOfAddinv,
  getCustomNumberOfClaims,
  getDocumentType,
  getFigureToBePublished,
  getNumberOfClaims,
  getNumberOfDrawings,
  hasType,
  specialDocumentsInAnotherLanguage,
  specialDocumentTypes,
  translationDocuments,
} from '../services/constants';
import draftApi from '../../../../api/draft.api';
import SimpleModal from '../../../common/simpleModal/SimpleModal';
import Tooltip from '../../../common/Tooltip';
import { getAttachmentConfig, getAttachmentLabel, typesWithoutPreview } from '../services/attachments.helper';
import useAlertDialog from '../../../../redux/features/alertDialog/useAlertDialog';
import { useAttachmentsConfig } from '../hooks/useAttachmentsConfig';
import { useAttachmentMetaFields } from '../uploader/meta/useAttachmentMetaFields';

const useStyles = makeStyles(theme => ({
  container: {
    alignItems: 'center',
    paddingBottom: '1rem',
  },
  fileIcon: {
    marginRight: '0.5rem',
    color: theme.palette.titleText,
  },
  lightText: {
    marginLeft: '0.3rem',
    color: theme.palette.titleText,
  },
  lightTextWithoutSpacing: {
    color: theme.palette.titleText,
  },
  strongText: {
    marginLeft: '0.3rem',
    color: theme.palette.titleText,
    fontWeight: 600,
  },
  attachmentInfoText: {
    color: theme.palette.input,
  },
  attachmentBody: {
    display: 'flex',
    alignContent: 'center',
    marginTop: '0.5rem',
  },
  attachmentTypeBody: {
    display: 'inline-block',
  },
  attachmentInfoBody: {
    display: 'inline-block',
    marginLeft: '2rem',
  },
  middleDot: {
    color: theme.palette.titleText,
    margin: '0 0.3rem',
  },
  actionsContainer: {
    display: 'contents',
    justifyContent: 'center',
    [theme.breakpoints.up('md')]: {
      display: 'flex',
    },
  },
  actionIcon: {
    cursor: 'pointer',
    color: theme.palette.titleText,
    backgroundColor: theme.palette.attachmentIconBackground || theme.palette.iconBackground,
    marginBottom: '0.75rem',
    '&:hover': {
      backgroundColor: theme.palette.attachmentIconBackgroundHover || theme.palette.iconBackground,
    },
  },
  topIdent: {
    marginTop: '0.75rem',
  },
}));

export function UploadedAttachmentItem({ draftId, attachment, onDeleteFile, readOnly, schema }) {
  const { t } = useTranslation();
  const [previewFile, setPreviewFile] = useState(undefined);
  const classes = useStyles();
  const { openAlertDialog } = useAlertDialog();
  const { attachmentTypesConfig } = useAttachmentsConfig();
  const attachmentConfig = getAttachmentConfig(
    attachmentTypesConfig,
    attachment.attachmentSubType,
    attachment.attachmentType,
  );
  const { extractDropdownDefinition } = useAttachmentMetaFields();

  const dropdownEnumDefinition = extractDropdownDefinition(attachmentConfig, schema);

  const onConfirmDeleteDialog = async () => {
    await onDeleteFile(attachment);
  };

  const handleItemDelete = async () => {
    openAlertDialog({
      updatedTexts: {
        title: 'messages.are-you-sure',
        message: 'messages.confirmation.attachment.delete',
        confirmButton: 'actions.delete',
        cancelButton: 'actions.cancel',
      },
      updatedSettings: {
        showCancelButton: true,
        showCloseButton: true,
      },
      acceptHandler: {
        func: onConfirmDeleteDialog,
      },
    });
  };

  const handleItemPreview = useCallback(async () => {
    const attachmentBlob = await callApi(draftApi.fetchAttachment)(
      draftId,
      attachment.id,
      attachment.originalFileName,
      attachment.mimeType,
    );

    if (attachmentBlob?.size > 0) {
      const attachmentObjectUrl = URL.createObjectURL(attachmentBlob);
      setPreviewFile(attachmentObjectUrl);
    }
  }, [draftId, attachment]);

  return (
    <Grid
      container
      spacing={1}
      key={attachment.id}
      className={classes.container}
      name="attachmentItemRow"
      id={`${attachment.attachmentType}attachmentItemRow`}
    >
      <Grid item xs={10} md={10}>
        <Grid className={classes.attachmentBody} name="attachmentBodyInfo">
          <AttachmentIcon classes={classes} attachment={attachment} />

          <Grid className={classes.attachmentTypeBody}>
            <Typography variant="body1" component="span" className={classes.lightTextWithoutSpacing}>
              {t('type.colon')}
            </Typography>

            <Typography variant="body1" component="span" className={classes.strongText}>
              {getAttachmentLabel(attachment)}
            </Typography>

            {attachment.attachmentType === specialDocumentTypes.COMBINED && (
              <Typography variant="body1" component="span" className={classes.lightText}>
                {`(${getAttachmentsTypesFromCombinedFile(attachment, t).join(', ')})`}
              </Typography>
            )}

            {(hasType(attachment, specialDocumentTypes.CLAIMS) ||
              hasType(attachment, specialDocumentTypes.DIFFERENT_CLAIMS) ||
              hasType(attachment, translationDocuments.TRANSLATION_CLAIMS) ||
              hasType(attachment, specialDocumentsInAnotherLanguage.CLAIMS_IN_ANOTHER_LANGUAGE) ||
              hasType(attachment, translationDocuments.TRANSLATION_CLAIMS_OTHER_LANGUAGE)) &&
              getNumberOfClaims(attachment) > 0 && (
              <>
                <Typography variant="body1" component="span" className={classes.lightText}>
                  |
                </Typography>
                <Typography variant="body1" component="span" className={classes.lightText}>
                  {t('claims.number.colon')}
                </Typography>
                <Typography variant="body1" component="span" className={classes.strongText}>
                  {getNumberOfClaims(attachment)}
                </Typography>
              </>
            )}

            {getCustomNumberOfClaims(attachment)?.map((customClaim) => {
              return (
                <>
                  <Typography variant="body1" component="span" className={classes.lightText}>
                    |
                  </Typography>
                  <Typography variant="body1" component="span" className={classes.lightText}>
                    {t(customClaim?.title)}
                  </Typography>
                  <Typography variant="body1" component="span" className={classes.strongText}>
                    {customClaim?.value}
                  </Typography>
                </>
              );
            })}

            {getCustomNumberOfAddinv(attachment)?.map((customAddinv) => {
              return (
                <>
                  <Typography variant="body1" component="span" className={classes.lightText}>
                    |
                  </Typography>
                  <Typography variant="body1" component="span" className={classes.lightText}>
                    {t(customAddinv?.title)}
                  </Typography>
                  <Typography variant="body1" component="span" className={classes.strongText}>
                    {customAddinv?.value}
                  </Typography>
                </>
              );
            })}

            {(hasType(attachment, specialDocumentTypes.ABSTRACT) ||
              hasType(attachment, specialDocumentsInAnotherLanguage.ABSTRACT_IN_ANOTHER_LANGUAGE) ||
              hasType(attachment, translationDocuments.TRANSLATION_ABSTRACT) ||
              hasType(attachment, translationDocuments.TRANSLATION_ABSTRACT_OTHER_LANGUAGE)) &&
              getFigureToBePublished(attachment) && (
              <>
                <Typography variant="body1" component="span" className={classes.lightText}>
                  |
                </Typography>
                <Typography variant="body1" component="span" className={classes.lightText}>
                  {t('attachment.figure.published.colon')}
                </Typography>
                <Typography variant="body1" component="span" className={classes.strongText}>
                  {getFigureToBePublished(attachment)}
                </Typography>
              </>
            )}

            {(hasType(attachment, specialDocumentTypes.DRAWINGS) ||
              hasType(attachment, specialDocumentsInAnotherLanguage.DRAWINGS_IN_ANOTHER_LANGUAGE) ||
              hasType(attachment, translationDocuments.TRANSLATION_DRAWINGS) ||
              hasType(attachment, translationDocuments.TRANSLATION_DRAWINGS_OTHER_LANGUAGE)) &&
              getNumberOfDrawings(attachment) > 0 && (
              <>
                <Typography variant="body1" component="span" className={classes.lightText}>
                  |
                </Typography>
                <Typography variant="body1" component="span" className={classes.lightText}>
                  {t('drawings.number.colon')}
                </Typography>
                <Typography variant="body1" component="span" className={classes.strongText}>
                  {getNumberOfDrawings(attachment)}
                </Typography>
              </>
            )}

            {hasType(attachment, specialDocumentTypes.OTHER) && getDocumentType(attachment) && (
              <>
                <Typography variant="body1" component="span" className={classes.strongText}>
                  |
                </Typography>
                <Typography variant="body1" component="span" className={classes.strongText}>
                  {t('attachments.type.document.colon')}
                </Typography>
                <Typography variant="body1" component="span" className={classes.lightText}>
                  {getDocumentType(attachment)}
                </Typography>
              </>
            )}
            {dropdownEnumDefinition && attachment[dropdownEnumDefinition.name] && (
              <>
                <Typography variant="body1" component="span" className={classes.lightText}>
                  |
                </Typography>
                <Typography variant="body1" component="span" className={classes.lightText}>
                  {dropdownEnumDefinition.title}
                </Typography>
                <Typography variant="body1" component="span" className={classes.strongText}>
                  {attachment[dropdownEnumDefinition.name]}
                </Typography>
              </>
            )}
          </Grid>
        </Grid>

        <Grid className={classes.attachmentInfoBody} name="attachmentItemInfo">
          <>
            <Typography variant="body1" component="span" className={classes.attachmentInfoText}>
              {toEllipsis(attachment.originalFileName, 40)}
            </Typography>
            <Typography variant="body1" component="span" className={classes.middleDot}>
              {String.fromCharCode(8226)}
            </Typography>
            <Typography variant="body1" component="span" className={classes.attachmentInfoText}>
              {shortFormatDatetime(attachment.createdDate)}
            </Typography>
            <Typography variant="body1" component="span" className={classes.middleDot}>
              {String.fromCharCode(8226)}
            </Typography>
            <Typography variant="body1" component="span" className={classes.attachmentInfoText}>
              {humanFileSize(attachment.bytes)}
            </Typography>
            {!typesWithoutPreview.includes(attachment.mimeType) && (
              <Typography variant="body1" component="span" className={classes.middleDot}>
                {String.fromCharCode(8226)}
              </Typography>
            )}
            {!typesWithoutPreview.includes(attachment.mimeType) &&
              attachment.attachmentType !== specialDocumentTypes.PRE_CONVERSION && (
              <Typography variant="body1" component="span" className={classes.attachmentInfoText}>
                {`${attachment.totalPages} ${attachment.totalPages > 1 ? t('pages') : t('page')}`}
              </Typography>
            )}
          </>
        </Grid>
      </Grid>
      <Grid item xs={2} md={2} className={classes.actionsContainer}>
        <div className={classes.topIdent}>
          {!typesWithoutPreview.includes(attachment.mimeType) && (
            <Tooltip title={t('Preview')} placement="top" arrow>
              <IconButton
                className={classes.actionIcon}
                aria-label={t('Preview')}
                data-testid="preview-button"
                alt="actions.attachments.preview.file"
                onClick={() => handleItemPreview()}
              >
                <VisibilityIcon />
              </IconButton>
            </Tooltip>
          )}

          {!typesWithoutPreview.includes(attachment.mimeType) && previewFile && (
            <SimpleModal
              isOpen={!!previewFile}
              onClose={() => setPreviewFile(undefined)}
              title={`${t('type.colon')} ${getAttachmentLabel(attachment)}`}
              subtitle={toEllipsis(attachment.originalFileName, 40)}
            >
              <embed
                type={attachment.mimeType}
                src={`${previewFile}`}
                alt={getAttachmentLabel(attachment)}
                width="100%"
                height="100%"
              />
            </SimpleModal>
          )}

          {!readOnly && (
            <>
              <Tooltip title={t('actions.delete')} placement="top" arrow>
                <IconButton
                  className={classes.actionIcon}
                  aria-label={t('actions.delete')}
                  data-testid="delete-button"
                  alt="actions.attachments.delete.file"
                  onClick={() => handleItemDelete()}
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            </>
          )}
        </div>
      </Grid>
    </Grid>
  );
}

const AttachmentIcon = ({ classes, attachment }) => {
  switch (attachment.fileExtension) {
    case 'pdf':
      return <PdfFileIcon className={classes.fileIcon} />;
    case 'jpg':
    case 'jpeg':
      return <Image className={classes.fileIcon} />;
    default:
      return <OtherFileIcon className={classes.fileIcon} />;
  }
};
